import React, { useState, useCallback } from 'react';

import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { IconButton } from 'ui-kit';
import { FIELD_CONFIG, OPERATOR_CONFIG } from 'configs/columns';

import { DealRole } from '__generated__/globalTypes';
// UI Constants
const CHEVRON_ICON_SIZE = 14;
const PERCENTAGE_MULTIPLIER = 100;
const BASIS_POINTS_DIVISOR = 10000;
const DECIMAL_PLACES = 3;

import {
  BidData,
  formatBidPrice,
  getBidDescription,
  getOwnershipBadge,
  getOwnershipLabel,
  groupBidsByBuyer,
} from './shared/bidUtils';
import { formatRelativeDateTime } from 'utils/dateUtils';

interface BuyerBidHistoryAccordionProps {
  bids: BidData[];
  selectedBid: BidData | null | undefined;
  dealId: string;
}

export const BuyerBidHistoryAccordion: React.FC<BuyerBidHistoryAccordionProps> = ({
  bids,
  selectedBid,
  dealId,
}) => {
  const [expandedBids, setExpandedBids] = useState<Set<string>>(new Set());

  const toggleExpanded = (bidId: string) => {
    const newExpanded = new Set(expandedBids);
    if (newExpanded.has(bidId)) {
      newExpanded.delete(bidId);
    } else {
      newExpanded.add(bidId);
    }
    setExpandedBids(newExpanded);
  };

  // Helper functions for formatting (same as original controller)
  const getFieldDisplayName = useCallback((fieldName: string) => {
    const config = Object.values(FIELD_CONFIG).find(
      (config) => config.filterSelector === fieldName,
    );
    return config?.string || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }, []);

  const getOperatorDisplayText = useCallback((operator: string) => {
    const operatorConfig = OPERATOR_CONFIG.find((config) => config.operator === operator);
    return operatorConfig?.operatorMiniString || operator;
  }, []);

  const groupedBids = groupBidsByBuyer(bids);

  if (!bids || bids.length === 0) {
    return <div className="py-8 text-center text-gray-400">No bids submitted yet.</div>;
  }

  return (
    <div className="w-full text-white">
      <ul className="space-y-1 overflow-x-auto">
        {groupedBids.map(({ buyerBid, counterOffers }) => {
          const hasCounterOffers = counterOffers.length > 0;
          const isBuyerBidExpanded = expandedBids.has(buyerBid.bid_id);
          const bidDescription = getBidDescription(buyerBid);
          const ownershipBadge = getOwnershipBadge(buyerBid);

          return (
            <React.Fragment key={buyerBid.bid_id}>
              {/* Main Buyer Bid Row - Using original styling */}
              <li
                className={`grid cursor-pointer grid-cols-[500px_250px_150px_1fr] items-center justify-between px-3 py-2 ${'border-l border-gray-800 hover:bg-brand-950'}`}
                onClick={() => toggleExpanded(buyerBid.bid_id)}
              >
                {/* Bid Description with Expansion - Original styling */}
                <div className="flex items-center gap-2">
                  <IconButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      toggleExpanded(buyerBid.bid_id);
                    }}
                    icon={
                      isBuyerBidExpanded ? (
                        <IconChevronDown size={CHEVRON_ICON_SIZE} />
                      ) : (
                        <IconChevronRight size={CHEVRON_ICON_SIZE} />
                      )
                    }
                    ariaLabel={isBuyerBidExpanded ? 'Collapse bid details' : 'Expand bid details'}
                    tooltipText={isBuyerBidExpanded ? 'Collapse bid details' : 'Expand bid details'}
                    tooltipPosition="top"
                  />
                  <span className="text-l font-medium text-white">
                    Your bid on {bidDescription}
                  </span>
                  {hasCounterOffers && (
                    <span className="ml-2 text-xs text-gray-400">
                      {counterOffers.length} counteroffer
                      {counterOffers.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Your Bid Price - Original styling */}
                <div className="flex items-center gap-2">
                  <span className="text-l tracking-tight">Your Bid</span>
                  <span className="text-l text-gray-400">{formatBidPrice(buyerBid)}</span>
                </div>

                {/* Ownership badge + Status - badge to the side of Your Bid column */}
                <div className="flex flex-wrap items-center gap-2">
                  {ownershipBadge && (
                    <span className="rounded bg-purple-800 px-2 py-0.5 text-xs text-purple-200">
                      {ownershipBadge}
                    </span>
                  )}
                  {buyerBid.status === 'ACCEPTED' && (
                    <span className="rounded bg-green-600 px-2 py-1 text-xs font-semibold text-green-100">
                      {buyerBid.status}
                    </span>
                  )}
                </div>

                {/* Date - Original styling */}
                <div className="flex items-center justify-end truncate">
                  <span className="text-sm text-gray-400">
                    {formatRelativeDateTime(buyerBid.created_date)}
                  </span>
                </div>
              </li>

              {/* Expanded Bid Details - Full detailed view */}
              {isBuyerBidExpanded && (
                <li className="ml-8 border-l-4 border-blue-500 bg-blue-900/20 p-4">
                  <div className="mb-6">
                    <div className="border-l-4 border-green-500 pl-4">
                      <h3 className="text-xl font-semibold text-slate-100">
                        {buyerBid.bidder === DealRole.BUYER
                          ? 'Your Bid Details'
                          : 'Seller Counteroffer Details'}
                      </h3>
                      <p className="mt-2 text-sm text-gray-400">
                        {buyerBid.carve && buyerBid.carve.length > 0 ? 'Carved pool' : 'Full pool'}{' '}
                        •
                        {buyerBid.status === 'ACCEPTED'
                          ? ' Accepted'
                          : buyerBid.status === 'PENDING'
                            ? ' Pending'
                            : ' Rejected'}
                        {ownershipBadge && (
                          <>
                            {' • '}
                            <span className="text-slate-300">{ownershipBadge}</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Two Column Layout like original BidCard */}
                  <div className="flex flex-nowrap gap-x-6">
                    {/* Left Column */}
                    <div className="flex w-[50%] flex-col gap-4">
                      {/* Carve Criteria Context */}
                      {buyerBid.carve && buyerBid.carve.length > 0 && (
                        <div className="rounded-lg border border-blue-600 bg-blue-900/20 p-4">
                          <h4 className="mb-3 text-lg font-medium text-blue-300">
                            {buyerBid.bidder === DealRole.BUYER
                              ? 'Your bid was for loans matching:'
                              : 'Seller counteroffer for loans matching:'}
                          </h4>
                          <div className="space-y-2">
                            {buyerBid.carve.map((filter, index) => (
                              <div key={index} className="text-sm">
                                <span className="font-medium text-white">
                                  {getFieldDisplayName(filter.field_name)}
                                </span>
                                <span className="text-gray-400">
                                  {' '}
                                  {getOperatorDisplayText(filter.operator)}{' '}
                                </span>
                                <span className="text-blue-300">{filter.operand || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Primary Metrics */}
                      <div>
                        <h4 className="mb-3 text-sm uppercase text-slate-200">Offer Details</h4>
                        <div className="w-full">
                          <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                            <span className="text-sm text-slate-200">Bid Price</span>
                            <span className="text-sm font-medium text-slate-200">
                              {buyerBid.bid_basis_points
                                ? `${((buyerBid.bid_basis_points / BASIS_POINTS_DIVISOR + 1) * PERCENTAGE_MULTIPLIER).toFixed(DECIMAL_PLACES)}%`
                                : 'N/A'}
                            </span>
                          </div>
                          {buyerBid.price?.rate_bps && (
                            <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                              <span className="text-sm text-slate-200">Net Yield @ Price</span>
                              <span className="text-sm font-medium text-slate-200">
                                {(buyerBid.price.rate_bps / PERCENTAGE_MULTIPLIER).toFixed(
                                  DECIMAL_PLACES,
                                )}
                                %
                              </span>
                            </div>
                          )}
                          {buyerBid.price?.price && (
                            <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                              <span className="text-sm text-slate-200">Purchase Price</span>
                              <span className="text-sm font-medium text-slate-200">
                                ${buyerBid.price.price.toLocaleString()}
                              </span>
                            </div>
                          )}
                          {buyerBid.price?.unpaid_balance && (
                            <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                              <span className="text-sm text-slate-200">UPB of Pool</span>
                              <span className="text-sm font-medium text-slate-200">
                                ${buyerBid.price.unpaid_balance.toLocaleString()}
                              </span>
                            </div>
                          )}
                          {buyerBid.price?.price && buyerBid.price?.unpaid_balance && (
                            <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                              <span className="text-sm text-slate-200">Price as % of UPB</span>
                              <span className="text-sm font-medium text-slate-200">
                                {(
                                  (buyerBid.price.price / buyerBid.price.unpaid_balance) *
                                  PERCENTAGE_MULTIPLIER
                                ).toFixed(DECIMAL_PLACES)}
                                %
                              </span>
                            </div>
                          )}
                          {/* Ownership: participation % or whole loan */}
                          <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                            <span className="text-sm text-slate-200">Ownership</span>
                            <span className="text-sm font-medium text-slate-200">
                              {getOwnershipLabel(buyerBid)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="flex w-[50%] flex-col gap-4">
                      {/* Additional bid information can go here */}
                      <div>
                        <h4 className="mb-3 text-sm uppercase text-slate-200">Bid Information</h4>
                        <div className="w-full">
                          <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                            <span className="text-sm text-slate-200">Status</span>
                            <span
                              className={`text-sm font-medium ${
                                buyerBid.status === 'ACCEPTED'
                                  ? 'text-green-400'
                                  : buyerBid.status === 'PENDING'
                                    ? 'text-yellow-400'
                                    : 'text-red-400'
                              }`}
                            >
                              {buyerBid.status}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                            <span className="text-sm text-slate-200">Submitted</span>
                            <span className="text-sm font-medium text-slate-200">
                              {formatRelativeDateTime(buyerBid.created_date)}
                            </span>
                          </div>
                          {buyerBid.bidder && (
                            <div className="flex justify-between border-t border-slate-600 px-3 py-2">
                              <span className="text-sm text-slate-200">Bidder</span>
                              <span className="text-sm font-medium text-slate-200">
                                {buyerBid.bidder}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )}

              {/* Expanded Counteroffers - Using original nested styling */}
              {isBuyerBidExpanded && hasCounterOffers && (
                <li className="ml-8">
                  <ul className="space-y-1">
                    {counterOffers.map((counterOffer, index) => {
                      const isCounterExpanded = expandedBids.has(counterOffer.bid_id);

                      return (
                        <React.Fragment key={counterOffer.bid_id}>
                          {/* Counteroffer Row */}
                          <li
                            className="bg-orange-900/20 hover:bg-orange-800/30 grid cursor-pointer grid-cols-[500px_250px_150px_1fr] items-center justify-between px-3 py-2"
                            onClick={() => toggleExpanded(counterOffer.bid_id)}
                          >
                            <div className="flex items-center gap-2">
                              <IconButton
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  toggleExpanded(counterOffer.bid_id);
                                }}
                                icon={
                                  isCounterExpanded ? (
                                    <IconChevronDown size={CHEVRON_ICON_SIZE} />
                                  ) : (
                                    <IconChevronRight size={CHEVRON_ICON_SIZE} />
                                  )
                                }
                                ariaLabel={
                                  isCounterExpanded
                                    ? 'Collapse counteroffer details'
                                    : 'Expand counteroffer details'
                                }
                                tooltipText={
                                  isCounterExpanded
                                    ? 'Collapse counteroffer details'
                                    : 'Expand counteroffer details'
                                }
                                tooltipPosition="top"
                              />
                              <span className="text-l text-orange-200 font-medium">
                                Seller counteroffer #{index + 1}
                              </span>
                            </div>

                            <div className="flex gap-2">
                              <span className="text-l text-orange-200 tracking-tight">
                                Seller Bid
                              </span>
                              <span className="text-l text-orange-300">
                                {formatBidPrice(counterOffer)}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              {counterOffer.status === 'ACCEPTED' && (
                                <span className="rounded bg-green-600 px-2 py-1 text-xs font-semibold text-green-100">
                                  {counterOffer.status}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center justify-end truncate">
                              <span className="text-orange-300 text-sm">
                                {formatRelativeDateTime(counterOffer.created_date)}
                              </span>
                            </div>
                          </li>

                          {/* Expanded Counteroffer Details */}
                          {isCounterExpanded && (
                            <li className="border-orange-500 bg-orange-900/20 ml-8 border-l-4 p-4">
                              <div className="mb-6">
                                <div className="border-orange-500 border-l-4 pl-4">
                                  <h3 className="text-orange-100 text-xl font-semibold">
                                    Seller Counteroffer Details
                                  </h3>
                                  <p className="text-orange-300 mt-2 text-sm">
                                    Response to your original bid of {formatBidPrice(buyerBid)}
                                  </p>
                                </div>
                              </div>

                              {/* Full counteroffer details in same format */}
                              <div className="flex flex-nowrap gap-x-6">
                                <div className="flex w-[50%] flex-col gap-4">
                                  <div>
                                    <h4 className="text-orange-200 mb-3 text-sm uppercase">
                                      Counteroffer Details
                                    </h4>
                                    <div className="w-full">
                                      <div className="border-orange-600 flex justify-between border-t px-3 py-2">
                                        <span className="text-orange-200 text-sm">
                                          Counteroffer Price
                                        </span>
                                        <span className="text-orange-200 text-sm font-medium">
                                          {counterOffer.bid_basis_points
                                            ? `${((counterOffer.bid_basis_points / BASIS_POINTS_DIVISOR + 1) * PERCENTAGE_MULTIPLIER).toFixed(DECIMAL_PLACES)}%`
                                            : 'N/A'}
                                        </span>
                                      </div>
                                      {counterOffer.price?.rate_bps && (
                                        <div className="border-orange-600 flex justify-between border-t px-3 py-2">
                                          <span className="text-orange-200 text-sm">
                                            Net Yield @ Price
                                          </span>
                                          <span className="text-orange-200 text-sm font-medium">
                                            {(
                                              counterOffer.price.rate_bps / PERCENTAGE_MULTIPLIER
                                            ).toFixed(DECIMAL_PLACES)}
                                            %
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex w-[50%] flex-col gap-4">
                                  <div>
                                    <h4 className="text-orange-200 mb-3 text-sm uppercase">
                                      Counteroffer Information
                                    </h4>
                                    <div className="w-full">
                                      <div className="border-orange-600 flex justify-between border-t px-3 py-2">
                                        <span className="text-orange-200 text-sm">Status</span>
                                        <span
                                          className={`text-sm font-medium ${
                                            counterOffer.status === 'ACCEPTED'
                                              ? 'text-green-400'
                                              : counterOffer.status === 'PENDING'
                                                ? 'text-yellow-400'
                                                : 'text-red-400'
                                          }`}
                                        >
                                          {counterOffer.status}
                                        </span>
                                      </div>
                                      <div className="border-orange-600 flex justify-between border-t px-3 py-2">
                                        <span className="text-orange-200 text-sm">Submitted</span>
                                        <span className="text-orange-200 text-sm font-medium">
                                          {formatRelativeDateTime(counterOffer.created_date)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </ul>
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

export default BuyerBidHistoryAccordion;
